import { useState } from 'react';
import { useIntl } from 'react-intl';
import MfaEnrollmentGateShell from '../auth/MfaEnrollmentGateShell';
import { useMfaStepperUi } from '../auth/mfa/MfaStepper';

/**
 * RD-SEC / RD-SEC-015 — Mandatory MFA enrollment interstitial for platform
 * super-admins. Platform super-admins can delete any tenant cross-tenant, so
 * MFA is REQUIRED, not optional. PlatformRoute renders this full-screen gate
 * (instead of the console) whenever a confirmed, active platform user has no
 * verified TOTP factor.
 *
 * RD-SEC-015: calls `useMfaStepperUi` (`mode="gate"`) directly — see
 * `TenantMfaEnrollmentGate` for the full contract (hold/complete semantics,
 * code-issue-failure never traps the user, and the 2026-09-25 anatomy fix
 * that routes `header`/`footer` into `MfaEnrollmentGateShell`'s pinned
 * regions instead of the scrollable body).
 *
 * PAIRED PATTERN: this gate and `TenantMfaEnrollmentGate` are a deliberate
 * mirror — both are thin wrappers around `MfaEnrollmentGateShell` +
 * `useMfaStepperUi`. Keep both in lockstep; a change here must land there too.
 */
export default function PlatformMfaEnrollmentGate({ onEnrolled, onSignOut, onRecoveryHoldChange }) {
  const intl = useIntl();
  const [footerHidden, setFooterHidden] = useState(false);

  const { header, body, footer } = useMfaStepperUi({
    mode: 'gate',
    onEnrollmentComplete: onEnrolled,
    onRecoveryHoldChange,
    onFooterHiddenChange: setFooterHidden,
  });

  return (
    <MfaEnrollmentGateShell
      ariaLabel={intl.formatMessage({ id: 'mfaStepper.name', defaultMessage: 'Two-step verification' })}
      stepHeader={header}
      footer={footer}
      onSignOut={onSignOut}
      hideFooter={footerHidden}
      signOutLabel={intl.formatMessage({
        id: 'mfaStepper.signOut',
        defaultMessage: 'Wrong account? Sign out',
      })}
    >
      {body}
    </MfaEnrollmentGateShell>
  );
}
